import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { PlatformService } from "@obg/ngx.global";
import { LoggingFactory } from "@obg/ngx.global.evo";
import { interval, merge, of, timer } from "rxjs";
import {
	catchError,
	exhaustMap,
	filter,
	map,
	mergeMap,
	retryWhen,
	switchMap,
	take,
	tap,
	timeoutWith,
} from "rxjs/operators";

import { AppError } from "../../app-error/app-error.model";
import { SESSION_ACTION_TYPE } from "../../auth/session.action";
import { MessageScopeType, MessageType } from "../../message/message.model";
import { MessageService } from "../../message/message.service";
import { CoreState } from "../../state";
import { AUTH_TRANSACTION_CODES } from "../auth-transaction.const";
import { HintCode, VerificationStatus } from "../auth-transaction.model";
import { OpenIdConnectService } from "../open-id-connect/open-id-connect.service";

import { DGA_AUTH_TRANSACTION_ACTION_TYPE, DgaAuthTransactionAction } from "./dga-auth-transaction.action";
import { DgaAuthTransactionConfigService } from "./dga-auth-transaction.config";
import { DGA_AUTH_TRANSACTION_CODES, DGA_AUTH_TRANSACTION_MESSAGE_CONTEXT } from "./dga-auth-transaction.const";
import {
	ExperianDataRequest,
	NemIdAuthInitializeSuccessPayload,
	NemIdVerificationStatusResponse,
	OpenIdConnectUrlRequest,
} from "./dga-auth-transaction.model";
import { DgaAuthTransactionSelector } from "./dga-auth-transaction.selector";
import { DgaAuthTransactionService } from "./dga-auth-transaction.service";

@Injectable()
export class DgaAuthTransactionEffect {
	getExperianData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DGA_AUTH_TRANSACTION_ACTION_TYPE.getExperianData),
			map((x: ActionWithPayload<ExperianDataRequest>) => x.payload),
			switchMap((token) =>
				this.service.getExperianData(token).pipe(
					map((response) => this.action.getExperianDataSuccess(response)),
					catchError((error: AppError) => of(this.action.getExperianDataFail(error)))
				)
			)
		)
	);

	setOpenIdConnectUrl$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DGA_AUTH_TRANSACTION_ACTION_TYPE.setOpenIdConnectUrl),
			map((x: ActionWithPayload<OpenIdConnectUrlRequest>) => x.payload),
			switchMap((provider) =>
				this.service.getOpenIdConnectUrl(provider).pipe(
					map((response) => this.action.setOpenIdConnectUrlSuccess(response.endpointUri)),
					catchError((error: AppError) => of(this.action.setOpenIdConnectUrlFail(error)))
				)
			)
		)
	);

	initializeOidcAuthTransaction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DGA_AUTH_TRANSACTION_ACTION_TYPE.initializeOidcAuthTransaction),
			map((x: ActionWithPayload) => x.payload),
			map((req) =>
				batchActions(
					this.action.setOpenIdConnectUrl({ provider: req.provider }),
					this.action.setCurrentJourney(req.provider),
					this.action.setCpr(req.cpr)
				)
			)
		)
	);

	setOpenIdConnectUrlSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DGA_AUTH_TRANSACTION_ACTION_TYPE.setOpenIdConnectUrlSuccess),
				map((x: ActionWithPayload<string>) => x.payload),
				switchMap((req) =>
					this.store.select(this.dgaAuthTransactionSelector.getCpr()).pipe(
						filter((cpr) => !!cpr),
						map((cpr) => this.openIdConnectService.navigateToOidcBroker(req, { cpr: cpr! })),
						catchError((error: AppError) => of(this.action.setOpenIdConnectUrlFail(error)))
					)
				)
			),
		{ dispatch: false }
	);

	initializePolling$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DGA_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionSuccess),
			map((x: ActionWithPayload<NemIdAuthInitializeSuccessPayload>) => x.payload),
			map((data) =>
				this.action.getVerificationStatus({
					transactionId: data.transactionId,
					authenticationType: data.authenticationType,
				})
			)
		)
	);

	getVerificationStatus$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus),
				map((x: ActionWithPayload<NemIdAuthInitializeSuccessPayload>) => x.payload),
				switchMap((request) =>
					interval(this.config.getVerificationStatusInterval()).pipe(
						exhaustMap(() => this.service.getStatus(request)),
						filter(
							(response: NemIdVerificationStatusResponse) =>
								response.status !== VerificationStatus.Pending
						),
						take(1),
						timeoutWith(
							this.config.getVerificationStatusTimeout(),
							of({
								status: VerificationStatus.Failed,
								hintCode: HintCode.Timeout,
							})
						),
						retryWhen((errors) =>
							errors.pipe(
								mergeMap((error, index) => {
									// IOS bug in WKWebView, which causes an xhr error
									if (error.data && error.data.isTrusted && index === 0) {
										return timer(0);
									}
									throw error;
								})
							)
						),
						map((response: NemIdVerificationStatusResponse) =>
							response.status === VerificationStatus.Failed
								? this.mapVerificationFailedResponse(response)
								: this.action.getVerificationStatusSuccess(response)
						),
						catchError((error: AppError) =>
							of(this.initializeFail(DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail, error))
						)
					)
				)
			)
		)
	);

	clearOnLoginRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.login, SESSION_ACTION_TYPE.loginWithTokenSuccess),
			map(() => this.action.reset())
		)
	);

	clearVerificationStatus$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginWithToken),
			map(() => this.action.resetVerificationStatus())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusFail]:
					DGA_AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				[DGA_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail]:
					DGA_AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction,
				[DGA_AUTH_TRANSACTION_ACTION_TYPE.getExperianDataFail]:
					DGA_AUTH_TRANSACTION_ACTION_TYPE.getExperianData,
			}),
			this.messageService.mapClearByContext$({
				[DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus]:
					DGA_AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				[DGA_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransaction]:
					DGA_AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction,
				[DGA_AUTH_TRANSACTION_ACTION_TYPE.getExperianData]: DGA_AUTH_TRANSACTION_ACTION_TYPE.getExperianData,
			}),
			this.messageService.map$({
				[DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatusSuccess]: {
					context: DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_TRANSACTION_CODES.verificationStatus.completed,
				},
				[DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus]: {
					context: DGA_AUTH_TRANSACTION_ACTION_TYPE.getVerificationStatus,
					scope: MessageScopeType.Local,
					type: MessageType.Info,
					messageCode: AUTH_TRANSACTION_CODES.verificationStatus.start,
				},
			})
		)
	);

	setCurrentJourney$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DGA_AUTH_TRANSACTION_ACTION_TYPE.setCurrentJourney),
				map((x: ActionWithPayload<string>) => x.payload),
				tap((provider) => this.service.setCurrentJourney(provider))
			),
		{ dispatch: false }
	);

	private logger = this.loggingFactory.get("DgaAuthTransactionEffect");

	constructor(
		private actions$: Actions,
		private platformService: PlatformService,
		private action: DgaAuthTransactionAction,
		private service: DgaAuthTransactionService,
		private config: DgaAuthTransactionConfigService,
		private messageService: MessageService,
		private loggingFactory: LoggingFactory,
		private dgaAuthTransactionSelector: DgaAuthTransactionSelector,
		private store: Store<CoreState>,
		private openIdConnectService: OpenIdConnectService
	) {}

	private initializeFail(actionType: string, error: AppError | Error): ActionWithPayload {
		switch (actionType) {
			default:
				this.logger.warn("getVerificationStatus$", JSON.stringify(error));
				return this.action.getVerificationStatusFail(error);
		}
	}

	private mapVerificationFailedResponse(response: NemIdVerificationStatusResponse): ActionWithPayload<any> {
		let error = DGA_AUTH_TRANSACTION_CODES.verificationStatus.failed.generic;

		switch (response.hintCode) {
			case HintCode.CPRNumberAlreadyRegistered:
				error = DGA_AUTH_TRANSACTION_CODES.verificationStatus.failed.cprNumberAlreadyRegistered;
				break;
			case HintCode.CPRNumberNotFound:
				error = DGA_AUTH_TRANSACTION_CODES.verificationStatus.failed.cprNumberNotFound;
				break;
		}

		return this.action.getVerificationStatusFail(new Error(error));
	}
}
